/**
 * Curated dashboard colour theme registry (IB-292).
 *
 * The ONE sanctioned source of literal hex for this story (mirrors
 * `accentPreference.ts`'s documented exception, design doc §5/AC9). Every
 * entry MUST independently pass IB-291's own `chartPalette.ts` gates
 * (`passesContrastFloor` WCAG>=3:1 for the 4 categorical values, both
 * diverging poles, and `seq-6`; `passesCvdDistinctness` ΔE>=15 for the 4
 * categorical values), for BOTH light and dark — enforced by
 * `dashboardThemePresets.test.ts`, not eyeballed. A theme that fails
 * derivation is never offered, by construction (design doc §1).
 *
 * "Platform Default" (AC7) is NOT a 5th hex family here — it is represented
 * by `null` throughout this feature's types (`DashboardThemeId | null`),
 * exactly the same "no entry = defer to the live derivation" pattern
 * `accentPreference.ts`'s `AccentPreference = string | null` already uses.
 * Selecting it removes any dashboard-theme override entirely so the
 * existing `AccentProvider`-computed `--iw-chart-*` (graphite default, or
 * the user's own IB-258 custom accent) shows through untouched.
 *
 * Hex values sourced from the already-ratified `ACCENT_PRESETS`
 * (`accentPreference.ts`) — never invented here (design doc §1):
 *   Ocean  = "Azure"   preset (#6E9BD1)
 *   Meadow = "Emerald" preset (#4FC79E)
 *   Amber  = "Amber"   preset (#D6A24E)
 *   Rose   = "Rose"    preset (#D48A9B)
 */

/** No entry for "Platform Default" — `null` already means that (see module header). */
export type DashboardThemeId = 'ocean' | 'meadow' | 'amber' | 'rose'

export interface DashboardThemePreset {
  id: DashboardThemeId
  name: string
  hex: string
}

export const DASHBOARD_THEME_PRESETS: readonly DashboardThemePreset[] = [
  { id: 'ocean', name: 'Ocean', hex: '#6E9BD1' },
  { id: 'meadow', name: 'Meadow', hex: '#4FC79E' },
  { id: 'amber', name: 'Amber', hex: '#D6A24E' },
  { id: 'rose', name: 'Rose', hex: '#D48A9B' },
]

/**
 * `true` when `value` is a member of the CURRENT registry above — the exact
 * mechanism behind AC8's "a removed/renamed id no longer in the current
 * registry falls back to Platform Default" (`dashboardThemePreference.ts`
 * calls this, rather than hard-coding a duplicate id list, so the fallback
 * always tracks the live registry, never a stale copy).
 */
export function isDashboardThemeId(value: unknown): value is DashboardThemeId {
  return typeof value === 'string' && DASHBOARD_THEME_PRESETS.some((preset) => preset.id === value)
}

/** The registered hex for a known id, or `null` if `id` is `null`/not currently registered. */
export function getDashboardThemePresetHex(id: DashboardThemeId | null): string | null {
  if (id === null) return null
  return DASHBOARD_THEME_PRESETS.find((preset) => preset.id === id)?.hex ?? null
}
